import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const defaultLockForYear = (year) => `${year}-08-28T00:00`;

const FantasyAdmin = ({ onMessage }) => {
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [payload, setPayload] = useState(null);
  const [year, setYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lockDate, setLockDate] = useState('');
  const [teamNames, setTeamNames] = useState('');
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [top25Ids, setTop25Ids] = useState(Array.from({ length: 25 }, () => ''));
  const [rankingLabel, setRankingLabel] = useState('Current');
  const [recapLabel, setRecapLabel] = useState('');
  const [recapText, setRecapText] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkFileName, setBulkFileName] = useState('');
  const [busy, setBusy] = useState(false);

  const say = (text) => {
    if (onMessage) onMessage(text);
  };

  const loadSeasons = useCallback(async () => {
    const res = await axios.get(`${apiUrl}/api/fantasy/seasons`, { headers: authHeaders() });
    const list = res.data || [];
    setSeasons(list.filter((season) => season.sport === 'cfb'));
    return list;
  }, []);

  const loadSeason = useCallback(async (id) => {
    if (!id) {
      setPayload(null);
      return;
    }
    const res = await axios.get(`${apiUrl}/api/fantasy/seasons/${id}`, { headers: authHeaders() });
    setPayload(res.data);
    const rankingTeams = res.data.ranking?.teams || [];
    setTop25Ids(Array.from({ length: 25 }, (_, index) => rankingTeams[index]?.teamId || ''));
  }, []);

  useEffect(() => {
    loadSeasons().catch(() => say('Could not load fantasy seasons.'));
  }, [loadSeasons]);

  useEffect(() => {
    if (seasonId) loadSeason(seasonId).catch(() => say('Could not load that season.'));
  }, [seasonId, loadSeason]);

  const teams = payload?.teams || [];
  const games = payload?.games || [];
  const season = payload?.season;

  const createSeason = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${apiUrl}/api/fantasy/seasons`, {
        sport: 'cfb',
        year: Number(year),
        startDate,
        endDate,
        lockDate,
      }, { headers: authHeaders() });
      say(`Created ${res.data.year} College Football Fantasy season as a draft.`);
      const list = await loadSeasons();
      const created = list.find((item) => item.id === res.data.id);
      setSeasonId(created?.id || res.data.id);
    } catch (error) {
      say(error.response?.data?.error || 'Could not create season.');
    } finally {
      setBusy(false);
    }
  };

  const activateSeason = async () => {
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/fantasy/seasons/${seasonId}/activate`, {}, { headers: authHeaders() });
      say('Season is live. Users can pick a team and four games.');
      await loadSeason(seasonId);
      await loadSeasons();
    } catch (error) {
      say(error.response?.data?.error || 'Could not activate season.');
    } finally {
      setBusy(false);
    }
  };

  const addTeams = async () => {
    const names = teamNames.split(/\n|,/).map((name) => name.trim()).filter(Boolean);
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/fantasy/seasons/${seasonId}/teams`, { names }, { headers: authHeaders() });
      setTeamNames('');
      say('Teams saved.');
      await loadSeason(seasonId);
    } catch (error) {
      say(error.response?.data?.error || 'Could not add teams.');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setBulkFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setBulkText(String(reader.result || ''));
    };
    reader.readAsText(file);
  };

  const uploadSchedule = async () => {
    if (!bulkText.trim()) {
      say('Paste or attach a CSV first.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${apiUrl}/api/fantasy/seasons/${seasonId}/schedule/bulk`,
        { csv: bulkText },
        { headers: authHeaders() }
      );
      const { teamsCreated, gamesCreated, gamesSkipped, errors, warnings } = res.data;
      const parts = [
        `Imported ${gamesCreated} games and ${teamsCreated} new teams for ${season.year}.`,
        `${gamesSkipped} duplicates skipped.`,
      ];
      if (errors && errors.length) {
        parts.push(`${errors.length} row error(s). First: line ${errors[0].line} — ${errors[0].error}`);
      }
      if (warnings && warnings.length) {
        parts.push(`${warnings.length} year warning(s).`);
      }
      say(parts.join(' '));
      setBulkText('');
      setBulkFileName('');
      await loadSeason(seasonId);
    } catch (error) {
      say(error.response?.data?.error || 'Could not import the schedule.');
    } finally {
      setBusy(false);
    }
  };

  const addGame = async () => {
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/fantasy/seasons/${seasonId}/games`, {
        date: gameDate,
        homeTeamId,
        awayTeamId,
      }, { headers: authHeaders() });
      setGameDate('');
      say('Game added to both teams’ schedules.');
      await loadSeason(seasonId);
    } catch (error) {
      say(error.response?.data?.error || 'Could not add game.');
    } finally {
      setBusy(false);
    }
  };

  const setWinner = async (gameId, winnerTeamId) => {
    setBusy(true);
    try {
      await axios.put(`${apiUrl}/api/fantasy/seasons/${seasonId}/games/${gameId}`, { winnerTeamId }, { headers: authHeaders() });
      await loadSeason(seasonId);
    } catch (error) {
      say(error.response?.data?.error || 'Could not save result.');
    } finally {
      setBusy(false);
    }
  };

  const saveTop25 = async () => {
    const ranked = top25Ids
      .map((teamId, index) => ({ teamId, rank: index + 1 }))
      .filter((item) => item.teamId);
    setBusy(true);
    try {
      await axios.put(`${apiUrl}/api/fantasy/seasons/${seasonId}/rankings`, {
        label: rankingLabel,
        teams: ranked,
      }, { headers: authHeaders() });
      say('Top 25 saved. You can calculate standings now.');
    } catch (error) {
      say(error.response?.data?.error || 'Could not save Top 25.');
    } finally {
      setBusy(false);
    }
  };

  const calculate = async () => {
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/fantasy/seasons/${seasonId}/calculate`, {
        weekLabel: recapLabel || 'Weekly update',
        text: recapText || 'Standings recalculated from current results and the latest Top 25.',
      }, { headers: authHeaders() });
      say('Standings calculated. Leaderboard and movement alerts are updated.');
    } catch (error) {
      say(error.response?.data?.error || 'Could not calculate standings.');
    } finally {
      setBusy(false);
    }
  };

  const deleteSeason = async () => {
    if (!season) return;
    const typed = window.prompt(
      `This permanently wipes the ${season.year} season: teams, schedule, user picks, Top 25 lists, recaps, and the leaderboard. Type ${season.year} to confirm.`
    );
    if (typed === null) return;
    if (String(typed).trim() !== String(season.year)) {
      say('Delete cancelled. The year did not match.');
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${apiUrl}/api/fantasy/seasons/${seasonId}`, { headers: authHeaders() });
      say(`${season.year} season wiped. You can create a clean season when you are ready.`);
      setSeasonId('');
      setPayload(null);
      await loadSeasons();
    } catch (error) {
      say(error.response?.data?.error || 'Could not delete season.');
    } finally {
      setBusy(false);
    }
  };

  const endSeason = async () => {
    if (!window.confirm('End this season and post the final leaderboard?')) return;
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/fantasy/seasons/${seasonId}/end`, {}, { headers: authHeaders() });
      say('Season ended. The final leaderboard is posted.');
      await loadSeason(seasonId);
      await loadSeasons();
    } catch (error) {
      say(error.response?.data?.error || 'Could not end season.');
    } finally {
      setBusy(false);
    }
  };

  const teamLabel = (id) => teams.find((team) => String(team.id) === String(id))?.name || id;

  return (
    <section className="admin-card">
      <h2>College Football Fantasy</h2>
      <p className="admin-footnote">
        No year or schedule is hardcoded. Create a season, load teams and games, then activate it.
        Lock defaults to August 28 if you leave that date as-is.
      </p>

      <h3>Start a season</h3>
      <label className="admin-label" htmlFor="fantasy-year">Year</label>
      <input
        id="fantasy-year"
        type="number"
        value={year}
        onChange={(e) => {
          const next = e.target.value;
          setYear(next);
          if (next && !lockDate) setLockDate(defaultLockForYear(next));
        }}
        placeholder="Season year"
      />
      <label className="admin-label" htmlFor="fantasy-start">Start date</label>
      <input id="fantasy-start" type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      <label className="admin-label" htmlFor="fantasy-end">End date</label>
      <input id="fantasy-end" type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      <label className="admin-label" htmlFor="fantasy-lock">Lock date (default Aug 28 midnight)</label>
      <input id="fantasy-lock" type="datetime-local" value={lockDate} onChange={(e) => setLockDate(e.target.value)} />
      <button type="button" onClick={createSeason} disabled={busy}>Create draft season</button>

      <label className="admin-label" htmlFor="fantasy-existing">Work on season</label>
      <select id="fantasy-existing" className="admin-select" value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
        <option value="">Select a CFB season</option>
        {seasons.map((item) => (
          <option key={item.id} value={item.id}>{item.year} · {item.status}</option>
        ))}
      </select>

      {season && (
        <>
          <p className="admin-footnote">
            Status: {season.status}. Users can play only after you activate a season that already has teams and a schedule.
          </p>
          <button type="button" onClick={activateSeason} disabled={busy || season.status !== 'draft'}>
            Activate season
          </button>
          <button type="button" className="admin-danger" onClick={endSeason} disabled={busy || season.status === 'final'}>
            End the season
          </button>
          <button type="button" className="admin-danger" onClick={deleteSeason} disabled={busy}>
            Delete / wipe this season
          </button>

          <h3>Bulk upload regular season</h3>
          <p className="admin-footnote">
            Attach this file to the selected {season.year} season. CSV header can be
            {' '}<code>homeTeam,awayTeam,date</code> or <code>year,homeTeam,awayTeam,date</code>.
            Dates like 2026-09-05 or 9/5/2026 work. Missing teams are created automatically, then each game
            shows on both teams’ schedules for playoff picks.
          </p>
          <input
            id="fantasy-bulk-file"
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={handleBulkFile}
          />
          {bulkFileName && <p className="admin-footnote">Loaded file: {bulkFileName}</p>}
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"homeTeam,awayTeam,date\nAlabama,Georgia,2026-09-05\nTexas,Ohio State,2026-09-12"}
            rows="8"
          />
          <button type="button" onClick={uploadSchedule} disabled={busy}>Upload schedule to this season</button>

          <h3>Teams</h3>
          <textarea
            value={teamNames}
            onChange={(e) => setTeamNames(e.target.value)}
            placeholder="One team per line or comma-separated"
          />
          <button type="button" onClick={addTeams} disabled={busy}>Add teams</button>
          <p className="admin-footnote">{teams.length} teams loaded.</p>

          <h3>Schedule</h3>
          <label className="admin-label" htmlFor="fantasy-game-date">Game date</label>
          <input id="fantasy-game-date" type="datetime-local" value={gameDate} onChange={(e) => setGameDate(e.target.value)} />
          <label className="admin-label" htmlFor="fantasy-away">Away</label>
          <select id="fantasy-away" className="admin-select" value={awayTeamId} onChange={(e) => setAwayTeamId(e.target.value)}>
            <option value="">Away team</option>
            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
          </select>
          <label className="admin-label" htmlFor="fantasy-home">Home</label>
          <select id="fantasy-home" className="admin-select" value={homeTeamId} onChange={(e) => setHomeTeamId(e.target.value)}>
            <option value="">Home team</option>
            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
          </select>
          <button type="button" onClick={addGame} disabled={busy}>Add game</button>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Matchup</th>
                  <th>Winner</th>
                </tr>
              </thead>
              <tbody>
                {games.map((game) => (
                  <tr key={game.id}>
                    <td>{new Date(game.date).toLocaleString()}</td>
                    <td>{teamLabel(game.awayTeamId)} @ {teamLabel(game.homeTeamId)}</td>
                    <td>
                      <select
                        className="admin-role-select"
                        value={game.winnerTeamId || ''}
                        onChange={(e) => setWinner(game.id, e.target.value)}
                      >
                        <option value="">Not played</option>
                        <option value={game.awayTeamId}>{teamLabel(game.awayTeamId)}</option>
                        <option value={game.homeTeamId}>{teamLabel(game.homeTeamId)}</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Top 25</h3>
          <input value={rankingLabel} onChange={(e) => setRankingLabel(e.target.value)} placeholder="Label, like Week 6 or Final" />
          {top25Ids.map((value, index) => (
            <label key={index} className="admin-label">
              #{index + 1}
              <select
                className="admin-select"
                value={value}
                onChange={(e) => setTop25Ids((current) => current.map((item, itemIndex) => (itemIndex === index ? e.target.value : item)))}
              >
                <option value="">Select team</option>
                {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </label>
          ))}
          <button type="button" onClick={saveTop25} disabled={busy}>Save Top 25</button>

          <h3>Calculate and recap</h3>
          <input value={recapLabel} onChange={(e) => setRecapLabel(e.target.value)} placeholder="Week label" />
          <textarea value={recapText} onChange={(e) => setRecapText(e.target.value)} placeholder="Optional mini-recap" />
          <button type="button" onClick={calculate} disabled={busy}>Calculate standings</button>
        </>
      )}
    </section>
  );
};

export default FantasyAdmin;
